/**
 * School user detail reads — the admin user-detail dialog's tabs on the web
 * dashboard.
 *
 * Several of these pass Blackbaud data through and the dialog also shows the
 * raw payload, so rows are loose objects: the fields below are the ones the
 * dashboard reads, and anything else on the wire is kept rather than stripped.
 */

import { z } from 'zod'

/** The `{ total, limit, offset, message }` page summary these reads carry. */
const ListMetaSchema = z.looseObject({
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  message: z.string().optional()
})

// ============================================================================
// GET /v1/schools/:school_public_id/assignments?school_user_id=
// ============================================================================

export const AssignmentLinkSchema = z.looseObject({
  url: z.string(),
  text: z.string(),
  type: z.string()
})

export const SchoolUserAssignmentStatusSchema = z.enum(['TODO', 'IN_PROGRESS', 'COMPLETED', 'OVERDUE', 'GRADED'])

const AssignmentTypeSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  weight: z.number()
})

export const SchoolUserAssignmentSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  nameProcessed: z.string(),
  nameLinks: z.array(AssignmentLinkSchema),
  description: z.string().nullable(),
  descriptionProcessed: z.string().nullable(),
  descriptionLinks: z.array(AssignmentLinkSchema),
  shortDescription: z.string().nullable(),
  type: z.string().nullable(),
  indexId: z.string().nullable(),
  publishOnAssigned: z.boolean(),
  platformSectionId: z.string().nullable(),
  dateAssigned: z.string().nullable(),
  dateDue: z.string().nullable(),
  /** Sent only to a caller who may see drafts (faculty, staff, admin); absent otherwise. */
  isPublished: z.boolean().optional(),
  isMajor: z.boolean(),
  isDiscussion: z.boolean(),
  isOnlineSubmission: z.boolean(),
  isAssessment: z.boolean(),
  isFormative: z.boolean(),
  includeInGradebook: z.boolean(),
  sections: z.array(
    z.looseObject({
      id: z.string(),
      platformId: z.string().nullable(),
      name: z.string(),
      offeringType: z.string().nullable(),
      isLeadSection: z.boolean(),
      course: z
        .looseObject({
          id: z.string(),
          platformId: z.string(),
          title: z.string().nullable(),
          description: z.string().nullable()
        })
        .nullable(),
      term: z
        .looseObject({
          id: z.string(),
          platformId: z.string(),
          description: z.string().nullable(),
          beginDate: z.string(),
          endDate: z.string()
        })
        .nullable(),
      enrolledCount: z.number().nullable(),
      gradedCount: z.number().nullable(),
      rank: z.number().nullable()
    })
  ),
  studentContext: z
    .looseObject({
      status: SchoolUserAssignmentStatusSchema,
      flags: z.looseObject({
        isExempt: z.boolean(),
        isIncomplete: z.boolean(),
        isLate: z.boolean(),
        isMissing: z.boolean()
      }),
      grade: z
        .looseObject({
          points: z.number().nullable(),
          maxPoints: z.number().nullable(),
          assignmentPercentage: z.number().nullable(),
          letterGrade: z.string().nullable(),
          comment: z.string().nullable(),
          sectionGradeAtTime: z.number().nullable(),
          markingPeriodId: z.string(),
          assignmentType: AssignmentTypeSchema.nullable()
        })
        .nullable()
        .optional(),
      lastUpdated: z.string().nullable()
    })
    .nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable()
})

export type SchoolUserAssignment = z.infer<typeof SchoolUserAssignmentSchema>

// ============================================================================
// GET /v1/school-users/:school_user_public_id/calendar?start_date=
// (Blackbaud calendar items passed through, camel-cased; Blackbaud may null any of them.)
// ============================================================================

export const SchoolUserCalendarEventSchema = z.looseObject({
  /** Never sent by this endpoint (the Calendar tab keys on it). */
  id: z.string().optional(),
  eventId: z.number(),
  title: z.string(),
  briefDescription: z.string().nullish(),
  longDescription: z.string().nullish(),
  startDate: z.string(),
  location: z.string().nullish(),
  buildingName: z.string().nullish(),
  roomName: z.string().nullish(),
  categoryName: z.string().nullish(),
  eventType: z.string().nullish(),
  allDay: z.boolean(),
  cancelled: z.boolean(),
  rescheduled: z.boolean(),
  rescheduleNote: z.string().nullish(),
  contactName: z.string().nullish(),
  contactEmail: z.string().nullish(),
  opponent: z.string().nullish(),
  homeAway: z.string().nullish(),
  tournament: z.boolean(),
  playoff: z.boolean(),
  invitational: z.boolean(),
  league: z.boolean(),
  scrimmage: z.boolean()
})

export type SchoolUserCalendarEvent = z.infer<typeof SchoolUserCalendarEventSchema>

/** The same handler as the school-scoped calendar below: a flat `{ data, meta, message }`. */
export const GetSchoolUserCalendarResponseSchema = z.object({
  data: z.array(SchoolUserCalendarEventSchema),
  meta: ListMetaSchema,
  message: z.string()
})

export type GetSchoolUserCalendarResponse = z.infer<typeof GetSchoolUserCalendarResponseSchema>

// ============================================================================
// GET /v1/schools/:school_public_id/school-users/:school_user_public_id/calendar?start_date=
// ============================================================================

export const SchoolCalendarEventSchema = z.looseObject({
  associationId: z.number(),
  briefDescription: z.string(),
  buildingName: z.string(),
  cancelled: z.boolean(),
  contactEmail: z.string(),
  contactName: z.string(),
  /** Absent when Blackbaud has no end date for the event. */
  endDate: z.string().optional(),
  eventId: z.number(),
  eventType: z.string(),
  categoryId: z.number(),
  categoryName: z.string(),
  homeAway: z.string(),
  invitational: z.boolean(),
  league: z.boolean(),
  location: z.string(),
  longDescription: z.string(),
  opponent: z.string(),
  playoff: z.boolean(),
  presetId: z.number(),
  /** Absent for a non-recurring Blackbaud event and for every iCal-sourced event. */
  recurrenceId: z.number().optional(),
  rescheduled: z.boolean(),
  rescheduleNote: z.string(),
  roomName: z.string(),
  scrimmage: z.boolean(),
  startDate: z.string(),
  title: z.string(),
  tournament: z.boolean(),
  allDay: z.boolean(),
  totalDays: z.number()
})

export type SchoolCalendarEvent = z.infer<typeof SchoolCalendarEventSchema>

export const GetSchoolUserSchoolCalendarResponseSchema = z.looseObject({
  data: z.array(SchoolCalendarEventSchema),
  meta: ListMetaSchema,
  message: z.string()
})

export type GetSchoolUserSchoolCalendarResponse = z.infer<typeof GetSchoolUserSchoolCalendarResponseSchema>

// ============================================================================
// GET /v1/schools/:school_public_id/school-users/:user_public_id/sections
// ============================================================================

const SectionReferenceSchema = z.looseObject({
  id: z.string(),
  name: z.string().nullable(),
  platformId: z.string(),
  abbreviation: z.string().nullable()
})

/**
 * One of the user's sections as `listSectionsService` sends it
 * (`PlatformSectionData`). The trailing optional fields are ones the Classes tab
 * reads that this endpoint never sends: they parse when absent, and the tab
 * falls back without them.
 */
export const SchoolUserSectionSchema = z.looseObject({
  id: z.string(),
  platformId: z.string(),
  platformLeadSectionId: z.string().nullable(),
  name: z.string(),
  offeringType: z.string(),
  effectiveVisible: z.boolean(),
  /** Present only when the caller may see this student's grade (SCH-1687); absent, not null, otherwise. */
  currentGradePercent: z.number().optional(),
  facilitators: z.array(
    z.looseObject({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string().nullable(),
      role: z.string()
    })
  ),
  personCount: z.number().nullable(),
  userGroups: z.array(
    z.looseObject({
      id: z.string(),
      name: z.string().nullish(),
      displayName: z.string().nullish(),
      description: z.string().nullish(),
      groupType: z.string().nullish(),
      audience: z.string().nullish(),
      source: z.string().nullish(),
      isSystemGroup: z.boolean().nullish(),
      lastSyncedAt: z.string().nullable(),
      memberCount: z.number()
    })
  ),
  assignments: z.array(
    z.looseObject({
      id: z.string().nullable(),
      name: z.string().nullable(),
      dueDate: z.string().nullable(),
      status: z.enum(['published', 'draft'])
    })
  ),
  syncStatus: z.enum(['SYNCED', 'NEVER_SYNCED', 'ERROR', 'PENDING']).optional(),
  schoolYear: z.looseObject({ id: z.string(), name: z.string().nullable(), platformId: z.string() }).nullable(),
  schoolLevel: SectionReferenceSchema.nullable(),
  gradeLevel: SectionReferenceSchema.nullable(),
  course: z
    .looseObject({
      id: z.string(),
      platformId: z.string(),
      code: z.string().nullable(),
      title: z.string().nullable(),
      /** Never sent by this endpoint. */
      credits: z.string().nullable().optional()
    })
    .nullable(),
  term: z
    .looseObject({
      id: z.string(),
      platformId: z.string(),
      description: z.string().nullable(),
      beginDate: z.string(),
      endDate: z.string()
    })
    .nullable(),
  // Never sent by this endpoint (the Classes tab reads them).
  displayName: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  isSystemGroup: z.boolean().optional(),
  memberCount: z.number().optional(),
  myMembership: z
    .looseObject({
      membershipPublicId: z.string(),
      permissionLevel: z.string(),
      identityRole: z.string(),
      sectionRole: z.string(),
      isLeader: z.boolean(),
      isHeadRole: z.boolean(),
      status: z.string(),
      joinedAt: z.string()
    })
    .nullable()
    .optional()
})

export type SchoolUserSection = z.infer<typeof SchoolUserSectionSchema>

export const GetSchoolUserSectionsResponseSchema = z.object({
  data: z.object({
    data: z.array(SchoolUserSectionSchema),
    meta: ListMetaSchema
  }),
  message: z.string()
})

export type GetSchoolUserSectionsResponse = z.infer<typeof GetSchoolUserSectionsResponseSchema>

// ============================================================================
// GET /v1/schools/:schoolId/school-users/:schoolUserId/credentials
// ============================================================================

/** The user's Blackbaud access token, at the root of the body (no `data` envelope). */
export const GetSchoolUserCredentialsResponseSchema = z.object({
  token: z.string(),
  expiresAt: z.string().nullable(),
  platform: z.string()
})

export type GetSchoolUserCredentialsResponse = z.infer<typeof GetSchoolUserCredentialsResponseSchema>

// ============================================================================
// GET /v1/schools/:school_public_id/school-users/:school_user_public_id/progress
// ============================================================================

const ProgressAssignmentSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  nameProcessed: z.string(),
  nameLinks: z.array(AssignmentLinkSchema),
  description: z.string().nullable(),
  descriptionProcessed: z.string().nullable(),
  descriptionLinks: z.array(AssignmentLinkSchema),
  type: z.string().nullable(),
  dateAssigned: z.string().nullable(),
  dateDue: z.string().nullable(),
  flags: z.looseObject({
    isPublished: z.boolean(),
    isMajor: z.boolean(),
    isAssessment: z.boolean(),
    isFormative: z.boolean(),
    includeInGradebook: z.boolean()
  }),
  grade: z
    .looseObject({
      points: z.number().nullable(),
      maxPoints: z.number().nullable(),
      assignmentPercentage: z.number().nullable(),
      letterGrade: z.string().nullable(),
      comment: z.string().nullable(),
      sectionGradeAtTime: z.number().nullable(),
      markingPeriodId: z.string(),
      assignmentType: AssignmentTypeSchema.nullable()
    })
    .nullable(),
  status: z
    .looseObject({
      /** The stored status; the sync writes `UNKNOWN` for a Blackbaud code it does not map. */
      status: z.union([SchoolUserAssignmentStatusSchema, z.literal('UNKNOWN')]).nullable(),
      isExempt: z.boolean(),
      isIncomplete: z.boolean(),
      isLate: z.boolean(),
      isMissing: z.boolean()
    })
    .nullable()
})

export const SchoolUserProgressSectionSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  offeringType: z.string().nullable(),
  course: z
    .looseObject({ id: z.string(), title: z.string().nullable(), description: z.string().nullable() })
    .nullable(),
  faculty: z.array(
    z.looseObject({ id: z.string(), firstName: z.string().nullable(), lastName: z.string().nullable() })
  ),
  currentGrade: z
    .looseObject({
      grade: z.number(),
      letterGrade: z.string().nullable(),
      markingPeriodId: z.string(),
      lastUpdated: z.string(),
      capturedDate: z.string()
    })
    .nullable(),
  gradeHistory: z.array(z.looseObject({ grade: z.number(), letterGrade: z.string().nullable(), date: z.string() })),
  assignments: z.array(ProgressAssignmentSchema)
})

export type SchoolUserProgressSection = z.infer<typeof SchoolUserProgressSectionSchema>

/** The progress read, at the root of the body (no `data` envelope). */
export const GetSchoolUserProgressResponseSchema = z.looseObject({
  student: z.looseObject({
    id: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    gradeLevel: z.string().nullable()
  }),
  currentTerm: z.looseObject({
    id: z.string(),
    platformId: z.string(),
    description: z.string().nullable(),
    beginDate: z.string(),
    endDate: z.string(),
    markingPeriodId: z.string(),
    schoolYearLabel: z.string().nullable()
  }),
  sections: z.array(SchoolUserProgressSectionSchema),
  summary: z.looseObject({
    totalSections: z.number(),
    sectionsWithGrades: z.number(),
    totalAssignments: z.number(),
    gradedAssignments: z.number(),
    averageGrade: z.number().nullable()
  })
})

export type GetSchoolUserProgressResponse = z.infer<typeof GetSchoolUserProgressResponseSchema>

// ============================================================================
// GET /v1/schools/:school_public_id/school-users/:school_user_public_id/resource-board
// ============================================================================

/** A Blackbaud resource passed through: its text fields may be null or absent. */
export const ResourceBoardItemSchema = z.looseObject({
  id: z.number(),
  title: z.string().nullish(),
  description: z.string().nullish(),
  photo: z.string().nullish(),
  type: z.string().nullish(),
  url: z.string().nullish(),
  roles: z.array(z.string()),
  /**
   * The item's place in the school's shared order (SCH-3172): 1-based, or
   * `null` when unplaced. The server already returns items in display order;
   * the key's PRESENCE tells a client to keep that order instead of sorting
   * A–Z itself. Absent from an older backend or a cached payload.
   */
  sortOrder: z.number().nullable().optional()
})

export type ResourceBoardItem = z.infer<typeof ResourceBoardItemSchema>

export const GetSchoolUserResourceBoardResponseSchema = z.object({
  data: z.array(ResourceBoardItemSchema),
  meta: ListMetaSchema,
  message: z.string()
})

export type GetSchoolUserResourceBoardResponse = z.infer<typeof GetSchoolUserResourceBoardResponseSchema>

// ============================================================================
// GET /v1/schools/:school_public_id/school-users/:school_user_public_id/schedule?start_date=
// GET /v1/schedules/faculty/:school_user_public_id?start_date=
// ============================================================================

export const ScheduleMeetingSchema = z.looseObject({
  id: z.string(),
  platformId: z.string(),
  meetingDate: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  blockId: z.number(),
  blockName: z.string(),
  roomId: z.number(),
  roomName: z.string(),
  roomNumber: z.string(),
  roomCapacity: z.number().nullable(),
  roomCode: z.string(),
  attendanceRequired: z.boolean()
})

export const ScheduleSectionSchema = z.looseObject({
  id: z.string(),
  platformId: z.string(),
  name: z.string(),
  courseTitle: z.string(),
  identifier: z.string(),
  offeringType: z.string(),
  faculty: z.array(
    z.looseObject({ id: z.string(), platformId: z.string(), firstName: z.string(), lastName: z.string() })
  )
})

export const ScheduleAssignmentSchema = z.looseObject({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  dueDate: z.coerce.date().optional()
})

/**
 * Why a student's schedule is not available (SCH-2501). `GRADE_HIDDEN`: the
 * student's grade is not on the school's `schedule.studentGradeLevels` list.
 */
export const ScheduleUnavailableReasonSchema = z.enum(['GRADE_HIDDEN'])

/**
 * A hidden student's schedule is a normal 200 with empty arrays plus this flag,
 * never an error status, so older app builds parse it and overwrite their cache
 * instead of falling back to a saved copy. Clients treat it as authoritative.
 */
export const ScheduleAvailabilitySchema = z.object({
  available: z.literal(false),
  reason: ScheduleUnavailableReasonSchema
})

export type ScheduleUnavailableReason = z.infer<typeof ScheduleUnavailableReasonSchema>
export type ScheduleAvailability = z.infer<typeof ScheduleAvailabilitySchema>

/** A student's week: meetings, the sections they belong to, and the assignments due; the zone may be unset. */
export const GetSchoolUserScheduleResponseSchema = z.object({
  message: z.string(),
  data: z.looseObject({
    schedule: z.array(ScheduleMeetingSchema),
    sections: z.array(ScheduleSectionSchema),
    assignments: z.array(ScheduleAssignmentSchema),
    /** `null` when the school has no configured time zone. */
    schoolTimeZone: z.string().nullable(),
    meta: ListMetaSchema,
    /** Present only when the schedule is hidden; the arrays are then empty. */
    availability: ScheduleAvailabilitySchema.optional()
  })
})

export type GetSchoolUserScheduleResponse = z.infer<typeof GetSchoolUserScheduleResponseSchema>

/** A teacher's week, in the student shape less the assignments; the zone may be unset. */
export const GetFacultyUserScheduleResponseSchema = z.object({
  message: z.string(),
  data: z.looseObject({
    schedule: z.array(ScheduleMeetingSchema),
    sections: z.array(ScheduleSectionSchema),
    schoolTimeZone: z.string().nullable().optional(),
    meta: ListMetaSchema
  })
})

export type GetFacultyUserScheduleResponse = z.infer<typeof GetFacultyUserScheduleResponseSchema>
